import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { isLocaleKey, mgadl, IMGADL, Doc_Progress } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { MyServiceContext } from '../..';
import InfoPopover from 'Components/InfoPopover';
import QuestionStepperSimpler from 'Components/QuestionStepperSimpler';
import { useIntl } from 'react-intl';
import colors from '../../../../../config/theme/colors';

const isFilledMgadl = (doc: IMGADL): boolean => mgadl.calculateProgress(doc).state === Doc_Progress.State.Completed;

export const mgadlSteps: Array<keyof IMGADL> = [
  'talking',
  'chewing',
  'swallowing',
  'breathing',
  'impairmentOfAbilityToBrushTeethOrCombHair',
  'impairmentOfAbilityToAriseFromAChair',
  'doubleVision',
  'eyelidDroop',
];

export const MGADLForm = ({ formData, editing, viewing, locale, clinicalForm = false }: IMGADLForm) => {
  const mgadlLocalizations = mgadl.localizations;
  const useLocale = isLocaleKey(locale) ? locale : 'fi';

  const [useManualScore, setUseManualScore] = React.useState(
    formData.document?.manualScore || formData.document?.manualScore === 0 ? true : false,
  );

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  /** Clear all score fields and manually entered score */
  const onChangeUseManual = () => {
    const clearedFieldsObj: IMGADL = {};
    const clearedFields = mgadlSteps.concat('manualScore');
    clearedFields.forEach(f => {
      if (formData.document?.[f] || formData.document?.[f] === 0) {
        clearedFieldsObj[f] = undefined;
      }
    });
    formData.onChange?.({
      ...formData.document,
      ...clearedFieldsObj,
    });
  };

  return (
    <div>
      {(!viewing || clinicalForm) && (
        <FormRow title={'general.date'}>
          <InputHandler name="date" type="PartialDate" dateDefault="now" formData={formData} editing={!!editing} />
        </FormRow>
      )}
      <FormRow title="myService.mgravis.manualScore">
        <InputHandler
          type="CheckboxSingle"
          editing={!viewing}
          name="useManualScore"
          formData={{
            onChange: () => {
              onChangeUseManual();
              setUseManualScore(!useManualScore);
            },
            document: { useManualScore: useManualScore },
          }}
          option="true"
          showLabel={true}
          optionFormatter={o => fm(`general.${o}`)}
        />
      </FormRow>
      {useManualScore ? (
        <FormRow title={mgadlLocalizations[useLocale]['totalScore']} formatTitle={false}>
          <InputHandler
            name="manualScore"
            type="NumberField"
            formData={formData}
            editing={!!editing}
            placeholder={fm('myService.score')}
            min={0}
            max={24}
          />
        </FormRow>
      ) : (
        <React.Fragment>
          <div style={{ padding: '1rem 0 2rem 0' }}>
            <QuestionStepperSimpler
              viewing={!editing}
              formData={formData}
              steps={mgadlSteps}
              stepsLocales={step =>
                `${mgadlSteps.indexOf(step as keyof IMGADL) + 1}. ` + mgadlLocalizations[useLocale][`labels.${step}`]
              }
              getStepContent={step => ({
                opts: [0, 1, 2, 3],
                optsLocale: (o: string | number) => mgadlLocalizations[useLocale][`opts.${step}.${o}`],
              })}
              simpleViewing
            />
          </div>
          <FormRow title={mgadlLocalizations[useLocale]['totalScore']} formatTitle={false}>
            <span style={{ fontWeight: 600 }}>
              {isFilledMgadl(formData.document) ? (
                mgadl.calculateProgress(formData.document).yielded + ' / 24'
              ) : (
                <InfoPopover
                  text={
                    <div>
                      <div>{fm('general.notFilled')}</div>
                      <div>{fm('myService.mgravis.notFilled')}</div>
                      {mgadlSteps
                        .filter(step => !(formData.document?.[step] || formData.document?.[step] === 0))
                        .map(s => (
                          <div key={s}>{`${mgadlSteps.indexOf(s) + 1}. ${
                            mgadlLocalizations[useLocale][`labels.${s}`]
                          }`}</div>
                        ))}
                    </div>
                  }
                  color="primary"
                />
              )}
            </span>
          </FormRow>
        </React.Fragment>
      )}

      <div style={{ fontStyle: 'italic', color: colors.darkGray, marginTop: '4.5rem' }}>
        {fm('functionalPerformanceMgravis.labels.mgadl.disclaimer')}
      </div>
    </div>
  );
};

interface IMGADLForm {
  formData: IFormData<IMGADL>;
  editing: boolean;
  viewing: boolean;
  locale: string;
  clinicalForm?: boolean;
}

export const MGADL = (): React.JSX.Element => {
  const myServContext = React.useContext(MyServiceContext);
  const { editing, setEditingData, locale, viewing, setViewingObj, setEditingObj } = myServContext;
  const mgadlData = ((editing?.data || viewing?.data) ?? {}) as IMGADL & IControlProps;

  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    setEditingData?.({
      ...mgadlData,
      ...values,
    });
  };

  return (
    <>
      <BlockWrapper
        title={editing ? formatPartialDate(mgadlData.date) : undefined}
        buttons={
          viewing
            ? [
                {
                  title: 'general.edit',
                  onClick: () => {
                    setViewingObj(null);
                    setEditingObj({ type: 'mgadl', data: mgadlData });
                  },
                },
              ]
            : undefined
        }
        topPadding
      >
        <MGADLForm
          formData={{ onChange: onChangeSurveyForm, document: mgadlData }}
          editing={!!editing}
          viewing={!!viewing}
          locale={locale}
        />
      </BlockWrapper>
    </>
  );
};
